import { useMemo } from "react";
import { Link, generatePath } from "react-router-dom";
import * as Team from "src/state/modules/team";
import * as Eden from "src/eden/content";
import { ROUTES } from "src/utils/constants/routes";
import { useGetTeamStatsByIDQuery, useGetTeamPlayersByIDQuery } from "src/state/modules/api";
import { Text } from "src/components/atoms/Text/Text";
import Styles from "./HotStreaks.module.scss";

// GameChanger only tracks active consecutive-game streaks for these three offensive stats. The
// streak blob (Eden.StatData) carries `general.gp` = the streak length in games and a full stat
// line accumulated over just the streak's games (so `h`/`rbi`/`hr` are the production *during* the
// streak, not the season). See the season-stats memo for the shape.
const STREAK_META: Record<string, { label: string; order: number; detail: (o: Eden.CoreBattingStats) => string }> = {
  streak_H: { label: "hitting streak", order: 0, detail: (o) => `${o.h}-for-${o.ab}` },
  streak_HR: { label: "home run streak", order: 1, detail: (o) => `${o.hr} HR` },
  streak_RBI: { label: "RBI streak", order: 2, detail: (o) => `${o.rbi} RBI` },
};

// A one-game "streak" isn't a streak; only surface genuinely active runs.
const MIN_STREAK_GAMES = 2;

type StreakLine = { key: string; games: number; label: string; detail: string };
type PlayerStreaks = { player: Team.Player; topStreak: number; streaks: StreakLine[] };

// Flattens the per-player streak map into display rows, keeping only meaningful (>= 2 game) streaks
// and only players still on the roster (so we can resolve a name + profile link). Players are sorted
// by their longest active streak; each player's streaks are sorted longest-first, then by stat.
function buildPlayerStreaks(streaks: Eden.SeasonStreaks, players: Team.Player[]): PlayerStreaks[] {
  const byId = new Map(players.map((p) => [p.id, p]));
  const result: PlayerStreaks[] = [];

  for (const [playerId, playerStreaks] of Object.entries(streaks)) {
    const player = byId.get(playerId);
    if (!player) continue;

    const lines: StreakLine[] = [];
    for (const [key, blob] of Object.entries(playerStreaks)) {
      const meta = STREAK_META[key];
      const games = blob.general?.gp ?? 0;
      if (!meta || games < MIN_STREAK_GAMES) continue;
      lines.push({ key, games, label: meta.label, detail: meta.detail(blob.offense) });
    }
    if (lines.length === 0) continue;

    lines.sort((a, b) => b.games - a.games || STREAK_META[a.key].order - STREAK_META[b.key].order);
    result.push({ player, topStreak: lines[0].games, streaks: lines });
  }

  return result.sort(
    (a, b) => b.topStreak - a.topStreak || a.player.last_name.localeCompare(b.player.last_name)
  );
}

const PlayerName = ({ player, teamId }: { player: Team.Player; teamId: string }) => {
  const name = `${player.first_name} ${player.last_name}`;
  const suffix = player.number != null && !Number.isNaN(player.number) ? ` #${player.number}` : "";
  if (player.status === "active") {
    return (
      <Link
        className={Styles.playerName}
        to={generatePath(ROUTES.teams.playerProfile, { pid: player.id, id: teamId })}
      >
        {name}
        <span className={Styles.number}>{suffix}</span>
      </Link>
    );
  }
  return (
    <span className={Styles.playerName}>
      {name}
      <span className={Styles.number}>{suffix}</span>
    </span>
  );
};

const StreakCard = ({ entry, teamId }: { entry: PlayerStreaks; teamId: string }) => (
  <div className={Styles.card} data-testid="hot-streak-card">
    <PlayerName player={entry.player} teamId={teamId} />
    <div className={Styles.streaks}>
      {entry.streaks.map((s) => (
        <div key={s.key} className={Styles.streakRow}>
          <span className={Styles.streakLabel}>
            <span className={Styles.flame} aria-hidden="true">
              🔥
            </span>
            <Text weight="bold">{s.games}</Text>
            <Text color="cool-grey-dark">{`-game ${s.label}`}</Text>
          </span>
          <Text size="small" color="cool-grey-dark" weight="semibold" className={Styles.detail}>
            {s.detail}
          </Text>
        </div>
      ))}
    </div>
  </div>
);

// Highlights players on an active hitting / HR / RBI streak, sourced from the team season-stats
// `streaks` block (GameChanger). Renders nothing when no team has a meaningful active streak, so it
// can sit unconditionally in a page layout without leaving an empty header.
const HotStreaks = ({ team }: { team: Team.Team }) => {
  const { data: stats } = useGetTeamStatsByIDQuery(team.id);
  const { data: players } = useGetTeamPlayersByIDQuery(team.id);

  const entries = useMemo(() => {
    const streaks = stats?.stats_data?.streaks;
    if (!streaks || !players) return [];
    return buildPlayerStreaks(streaks, players);
  }, [stats, players]);

  if (entries.length === 0) return null;

  return (
    <section className={Styles.section}>
      <Text block size="xlarge" weight="xbold" className={Styles.sectionTitle}>
        Hot Streaks
      </Text>
      <div className={Styles.cards}>
        {entries.map((entry) => (
          <StreakCard key={entry.player.id} entry={entry} teamId={team.id} />
        ))}
      </div>
    </section>
  );
};

export default HotStreaks;
